import { ControllerHelper, WikiController, WikiSearchResultDto } from 'collaboration-service';
import AutocompleteSearch from 'components/AutocompleteSearch/AutocompleteSearch';
import { ExtendedDataCollection } from 'imaginarity-azure';
import { FormElement, Icon, ImgThemeConsumer, Input, LoaderInline } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { ChangeEvent, InputProps, SuggestionsFetchRequestedParams } from 'react-autosuggest';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'services/ApplicationState/ApplicationState';
import { useImgI18N } from 'services/ImgI18N';

import { WikiSC as T } from './WikiSC';

export interface WikiSearchProps {
    groupId?: string;
    searchText: string;
    postType: string;
    lng?: string;
    onSearched?: (posts?: ExtendedDataCollection<WikiSearchResultDto, string>) => void;
    onSearchStarted?: (term: string) => void;
    inlineLoader?: boolean;
    dark?: boolean;
}

const WikiSearch: React.FC<WikiSearchProps> = ({
    groupId,
    searchText,
    lng,
    onSearched,
    onSearchStarted,
    inlineLoader,
    dark
}) => {
    const { t } = useImgI18N('playlist');
    const i18n = useSelector((state: ApplicationState) => state.i18n);

    const [state, setState] = React.useState({
        search: "",
        suggestions: [] as string[],
        searchActive: false
    });

    const search = React.useMemo(() => _.debounce(async (text: string) => {
        const currentLng = lng || i18n.currentLanguage;

        if (onSearchStarted) {
            onSearchStarted(text);
        }

        const searchResults = await ControllerHelper.promise(
            {
                text,
                lng: currentLng,
                filter: groupId ? { onlyInGroups: [groupId] } : undefined
            },
            WikiController.Search
        );
        searchResults.data = text;

        if (onSearched) {
            onSearched(searchResults);
        }

        setState(prev => ({ ...prev, searchActive: false }));
    }, 50), [groupId, lng, i18n.currentLanguage, onSearched, onSearchStarted]);

    const clearInput = React.useCallback(() => {
        setState(prev => ({ ...prev, search: "" }));
        if (onSearched) {
            onSearched(undefined);
        }
    }, [onSearched]);

    const suggestionSelected = React.useCallback((e: any, data: { suggestion: string }) => {
        const prefixEnd = state.search.lastIndexOf(" ");
        let prefix = "";
        if (prefixEnd >= 0) {
            prefix = state.search.slice(0, prefixEnd) + " ";
        }

        setState(prev => ({ ...prev, searchActive: true }));
        search(`${prefix}${data.suggestion}`);
    }, [state.search, search]);

    const searchChanged = React.useCallback((event: React.FormEvent<any>, params: ChangeEvent) => {
        const text = params.newValue ? params.newValue : "";
        setState(prev => ({ ...prev, search: text }));
    }, []);

    const suggestionFetchRequested = React.useCallback((request: SuggestionsFetchRequestedParams) => {
        if (request.reason === "input-changed" || request.reason === "input-focused") {
            let terms = request.value.split(" ");
            terms = _.filter(terms, t => t.length > 0);
            const lastTerm = _.last(terms);
            if (lastTerm) {
                const currentLng = lng || i18n.currentLanguage;
                WikiController.SuggestTerm(
                    {
                        term: lastTerm,
                        lng: currentLng,
                        groupIds: groupId ? [groupId] : undefined
                    },
                    suggestions => {
                        setState(prev => ({ ...prev, suggestions }));
                    }
                );
            }
        }
    }, [groupId, lng, i18n.currentLanguage]);

    const enterPressed = React.useCallback(() => {
        if (state.search.length > 0) {
            setState(prev => ({ ...prev, searchActive: true }));
            search(state.search);
        } else {
            if (onSearched) {
                onSearched(undefined);   // signal to reload as usual
            }
        }
    }, [state.search, search, onSearched]);

    const suggestionsClearRequested = React.useCallback(() => {
        setState(prev => ({ ...prev, suggestions: [] }));
        if (onSearched) {
            onSearched(undefined);   // signal to reload as usual
        }
    }, [onSearched]);

    const renderInputComponent = React.useCallback((marginTop: string) => (inputProps: InputProps<string>) => {
        const { onChange, onBlur, width, size, ref, ...rest } = inputProps as any;
        const imgOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onChange(event, { newValue: event.target.value, method: "type" });
        };

        return (
            <div style={{ marginTop }}>
                <div style={{ width: dark ? "100%" : "calc(100% - 30px)", float: "right", marginRight: -5 }}>
                    <FormElement
                        element={Input}
                        width="calc(100% - 10px)"
                        onChange={imgOnChange}
                        kind="default"
                        icon="search"
                        noMargin
                        innerRef={ref}
                        {...rest}
                    />
                    {state.search !== "" && (
                        <T.ResetSearchIcon onClick={clearInput}>
                            <Icon name="times" color={"@mainForeground"} />
                        </T.ResetSearchIcon>
                    )}
                    {inlineLoader && (
                        <div style={{ width: "calc(100% - 18px)", marginLeft: 4 }}>
                            <LoaderInline
                                active={state.searchActive}
                                infoText={t("searching for {{word}}", { word: state.search })}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }, [state.search, state.searchActive, dark, inlineLoader, clearInput, t]);

    return (
        <ImgThemeConsumer>
            {theme => (
                <AutocompleteSearch
                    placeholder={searchText}
                    search={state.search}
                    searchChanged={searchChanged}
                    suggestions={state.suggestions}
                    onEnterPressed={enterPressed}
                    onSuggestionsFetchRequested={suggestionFetchRequested}
                    onSuggestionsClearRequested={suggestionsClearRequested}
                    onSuggestionSelected={suggestionSelected}
                    renderInputComponent={renderInputComponent("0")}
                    theme={{
                        container: { position: "relative" },
                        suggestionsContainer: {
                            display: "none",
                            width: "calc(100% - 48px)",
                            marginLeft: 39,
                        },
                        suggestionsContainerOpen: {
                            background: theme.colors.mainBackground,
                            backgroundColor: theme.colors.mainBackground,
                            display: "block",
                            position: "absolute",
                            top: 45,
                            border: "1px solid " + theme.colors.accent,
                            fontSize: "1em",
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                            zIndex: 3000
                        },
                        suggestionsList: {
                            margin: 0,
                            padding: 0,
                            listStyleType: "none",
                            paddingTop: 0,
                            paddingBottom: 0
                        },
                        suggestion: {
                            paddingTop: 10,
                            paddingBottom: 10,
                            paddingLeft: 10,
                            cursor: "pointer"
                        },
                        suggestionHighlighted: {
                            background: theme.colors.accent,
                            backgroundColor: theme.colors.accent,
                            color: theme.colors.mainBackground
                        }
                    }}
                />
            )}
        </ImgThemeConsumer>
    );
};

export default WikiSearch;